import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import type {
  ContactRequest,
  SiteRequest,
  SiteResponse,
} from 'src/api/ProactApiClient';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {BackBar} from 'src/app/layout/BackBar';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkClients} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useGetClientSelectOptionsQuery} from 'src/redux/queries/client';
import {
  useCreateSiteMutation,
  useGetSiteQuery,
  useUpdateSiteMutation,
} from 'src/redux/queries/site';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {ContactsEditor} from './ContactsFields';
import {
  MapSiteResponseToSiteRequest,
  SiteDocumentTitle,
  SiteFormMeta,
} from './siteHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface SiteFormProps {
  id?: number;
  mode: 'create' | 'edit';
}

export const SiteForm: React.FC<SiteFormProps> = ({id, mode}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';
  const fromClientId = isCreateMode ? searchParams.get('clientId') : null;

  const {data: clients, isLoading: isClientsLoading} =
    useGetClientSelectOptionsQuery({
      start: 0,
      length: 10000,
      filters: [],
      filterFns: [],
      sorting: [],
    });

  const {data: fetchedSite, isLoading: isSiteLoading} = useGetSiteQuery(id!, {
    skip: !isEditMode || id === undefined,
  });

  const [createSite] = useCreateSiteMutation();
  const [updateSite] = useUpdateSiteMutation();

  const item: SiteResponse | undefined = fetchedSite;
  const isLoading = isEditMode && isSiteLoading;

  const formMeta = useMemo<IFormFieldV2[]>(
    () => (isClientsLoading ? [] : SiteFormMeta(clients?.data ?? [])),
    [isClientsLoading, clients]
  );

  const form = useEntitySyncedForm({
    formMeta,
    entity: fetchedSite,
    toFormData: toFormDataV2,
    initialData: fromClientId ? {clientId: fromClientId} : undefined,
  });

  const handleSave = async () => {
    setIsSavePending(true);

    try {
      const values = form.getFieldValues();
      const contacts = (form.methods.getValues('contacts') ??
        []) as ContactRequest[];
      const model: SiteRequest = MapSiteResponseToSiteRequest({
        ...values,
        contacts,
        id: item?.id,
      } as SiteResponse);

      let response: SiteResponse;

      if (isCreateMode) {
        response = await createSite(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
      } else {
        response = await updateSite(model).unwrap();
        setAlert(`${item?.name ?? 'Site'} was successfully updated`, 'success');
      }

      if (isCreateMode && fromClientId) {
        navigate(PathUtility.ClientsView(fromClientId), {replace: true});
      } else {
        navigate(PathUtility.SitesView(response.id), {replace: true});
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.SitesView(id)
      : fromClientId
      ? PathUtility.ClientsView(fromClientId)
      : PathUtility.Clients('sites')
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode ? 'New Site' : item?.name ?? 'Edit Site';

  return (
    <Stack>
      <BackBar
        fallbackTo={
          isEditMode && id
            ? PathUtility.SitesView(id)
            : fromClientId
            ? PathUtility.ClientsView(fromClientId)
            : PathUtility.Clients('sites')
        }
        fallbackLabel={
          isEditMode
            ? 'Back to Site'
            : fromClientId
            ? 'Back to Client'
            : 'Back to Sites'
        }
        breadcrumbs={[
          {label: SiteDocumentTitle, to: PathUtility.Clients('sites')},
          ...(isEditMode && item
            ? [{label: item.name ?? 'Site', to: PathUtility.SitesView(id!)}]
            : []),
          {label: isCreateMode ? 'New Site' : 'Edit'},
        ]}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkClients.icon}>
          {pageTitle}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create Site' : 'Save Changes'}
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <ProactFormGrid />
        <ContactsEditor />
      </ProactForm>

      <Group>
        <PageHeader.Action variant="cancel" onClick={handleCancel}>
          Cancel
        </PageHeader.Action>
        <PageHeader.Action
          variant={isCreateMode ? 'create' : 'save'}
          onClick={handleValidateAndSave}
          loading={isSavePending}>
          {isCreateMode ? 'Create Site' : 'Save Changes'}
        </PageHeader.Action>
      </Group>
    </Stack>
  );
};
